import { simplify } from '@turf/simplify'
import { z } from 'zod'
import { CountryIso3Schema, IsoDateTimeSchema, LatLngSchema, LatitudeSchema, LongitudeSchema } from '../primitives'

/**
 * Residence areas (PLAN v4 §1.1 as amended by R12): the union of 1–20 areas an organization's members must live in.
 * Stored FROZEN in `requirements.residence.areas` (a later OSM edit never changes eligibility).
 *
 * - `place`: a named boundary picked through the geocoding proxy (Nominatim relation / way). Countries are `place`
 *   areas with `adminLevel: 2` and `countryIso3` (R12: no separate country kind); evidence without coordinates but
 *   with a country code matches them by code.
 * - `polygon`: drawn by the organizer (polygon / rectangle).
 * - `circle`: centre + radius.
 *
 * GeoJSON order is `[lng, lat]`; Leaflet is `[lat, lng]`: convert with `toLatLng` / `toPosition` only.
 */

/* ------------------------------------------------------------------ caps */

/** Max vertices of one area geometry (after simplification). */
export const MAX_AREA_POINTS = 5_000
/** Max serialized size of one area geometry (JSON bytes, coordinates rounded to 5 decimals). */
export const MAX_AREA_BYTES = 100_000
/** Max areas per organization. */
export const MAX_AREAS = 20
/** Max vertices over every area of an organization. */
export const MAX_TOTAL_AREA_POINTS = 20_000
/** Geometry sent to the public landing map (simplified further, see `publicArea`). */
export const MAX_PUBLIC_AREA_POINTS = 1_000
export const MAX_PUBLIC_AREA_BYTES = 30_000
/** Coordinates are rounded to 5 decimals (≈1.1 m). */
export const COORDINATE_DECIMALS = 5
/** km per degree of latitude (simplification tolerance → error bound). */
export const KM_PER_DEGREE = 111.32

/* ------------------------------------------------------------------ GeoJSON */

export const PositionSchema = z.tuple([LongitudeSchema, LatitudeSchema])
export type Position = z.infer<typeof PositionSchema>
export const RING_NOT_CLOSED_MESSAGE = 'Ring must be closed (first point = last point)'

/** Closed ring (first = last), at least 4 positions. Applies to outer rings and holes. */
export const LinearRingSchema = z
  .array(PositionSchema)
  .min(4)
  .refine(
    (ring) => {
      const a = ring[0]
      const b = ring[ring.length - 1]
      return !!a && !!b && a[0] === b[0] && a[1] === b[1]
    },
    { message: RING_NOT_CLOSED_MESSAGE }
  )

export const PolygonGeometrySchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(LinearRingSchema).min(1),
})
export type PolygonGeometry = z.infer<typeof PolygonGeometrySchema>

export const MultiPolygonGeometrySchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(LinearRingSchema).min(1)).min(1),
})
export type MultiPolygonGeometry = z.infer<typeof MultiPolygonGeometrySchema>

export type AreaGeometry = PolygonGeometry | MultiPolygonGeometry

/** `[west, south, east, north]` in degrees. */
export const BboxSchema = z.tuple([LongitudeSchema, LatitudeSchema, LongitudeSchema, LatitudeSchema])
export type Bbox = z.infer<typeof BboxSchema>

/** Number of positions of a geometry (every ring, closing positions included). */
export function geometryPointCount(g: AreaGeometry): number {
  if (g.type === 'Polygon') return g.coordinates.reduce((n, ring) => n + ring.length, 0)
  return g.coordinates.reduce((n, poly) => n + poly.reduce((m, ring) => m + ring.length, 0), 0)
}

export const geometryBytes = (g: AreaGeometry): number => JSON.stringify(g).length

const withinCaps = (g: AreaGeometry, ctx: z.RefinementCtx) => {
  const points = geometryPointCount(g)
  if (points > MAX_AREA_POINTS)
    ctx.addIssue({ code: 'custom', message: `Area too detailed: ${points} points (max ${MAX_AREA_POINTS})` })
  const bytes = geometryBytes(g)
  if (bytes > MAX_AREA_BYTES)
    ctx.addIssue({ code: 'custom', message: `Area too large: ${bytes} bytes (max ${MAX_AREA_BYTES})` })
}

/** Stored area geometry: Polygon or MultiPolygon (holes allowed), within the caps. */
export const AreaGeometrySchema = z
  .discriminatedUnion('type', [PolygonGeometrySchema, MultiPolygonGeometrySchema])
  .superRefine(withinCaps)

/** Drawn areas: a single Polygon (polygon / rectangle tool). */
export const DrawnGeometrySchema = PolygonGeometrySchema.superRefine(withinCaps)

/* ------------------------------------------------------------------ areas */

const AreaLabelSchema = z.string().trim().min(1, 'A name is required').max(200)

export const OSM_TYPES = ['N', 'W', 'R'] as const
export const OsmTypeSchema = z.enum(OSM_TYPES)
export type OsmType = z.infer<typeof OsmTypeSchema>

export const PlaceAreaSchema = z.object({
  kind: z.literal('place'),
  /** Display name in the organizer's language ("Barcelona, Catalunya, España"). */
  label: AreaLabelSchema,
  osmType: OsmTypeSchema,
  osmId: z.number().int().positive(),
  /** Nominatim `addresstype` (city, state, state_district, country, …). */
  placeType: z.string().trim().min(1).max(60),
  /** OSM `admin_level` (2 = country) when known. */
  adminLevel: z.number().int().min(1).max(15).nullable().default(null),
  /** Set for countries (admin_level 2): evidence without coordinates matches by country code (R12). */
  countryIso3: CountryIso3Schema.nullable().default(null),
  geometry: AreaGeometrySchema,
  bbox: BboxSchema,
  /** Douglas-Peucker tolerance used (degrees): its error (×111 km) is added to every evidence uncertainty. */
  simplifyToleranceDeg: z.number().min(0).max(1).default(0),
  source: z.literal('osm').default('osm'),
  license: z.literal('ODbL-1.0').default('ODbL-1.0'),
  fetchedAt: IsoDateTimeSchema,
})
export type PlaceArea = z.infer<typeof PlaceAreaSchema>

export const PolygonAreaSchema = z.object({
  kind: z.literal('polygon'),
  label: AreaLabelSchema,
  geometry: DrawnGeometrySchema,
})
export type PolygonArea = z.infer<typeof PolygonAreaSchema>

export const MIN_CIRCLE_RADIUS_KM = 0.1
export const MAX_CIRCLE_RADIUS_KM = 1_000

export const CircleAreaSchema = z.object({
  kind: z.literal('circle'),
  label: AreaLabelSchema,
  center: LatLngSchema,
  radiusKm: z.number().min(MIN_CIRCLE_RADIUS_KM).max(MAX_CIRCLE_RADIUS_KM),
})
export type CircleArea = z.infer<typeof CircleAreaSchema>

export const AREA_KINDS = ['place', 'polygon', 'circle'] as const
export const AreaKindSchema = z.enum(AREA_KINDS)
export type AreaKind = z.infer<typeof AreaKindSchema>

export const AreaSchema = z.discriminatedUnion('kind', [PlaceAreaSchema, PolygonAreaSchema, CircleAreaSchema])
export type Area = z.infer<typeof AreaSchema>
export type AreaInput = z.input<typeof AreaSchema>

/* ------------------------------------------------------------------ helpers */

export const toLatLng = ([lng, lat]: readonly [number, number] | number[]) => ({ lat: lat!, lng: lng! })
export const toPosition = (p: { lat: number; lng: number }): Position => [p.lng, p.lat]

const round = (n: number) => Number(n.toFixed(COORDINATE_DECIMALS))

function polygonsOf(g: AreaGeometry): Position[][][] {
  return g.type === 'Polygon' ? [g.coordinates as Position[][]] : (g.coordinates as Position[][][])
}

export function geometryBbox(g: AreaGeometry): Bbox {
  let [w, s, e, n] = [180, 90, -180, -90]
  for (const poly of polygonsOf(g))
    for (const ring of poly)
      for (const [lng, lat] of ring) {
        if (lng < w) w = lng
        if (lng > e) e = lng
        if (lat < s) s = lat
        if (lat > n) n = lat
      }
  return [w, s, e, n]
}

/** Bounding box of any area (circles: approximate, from the radius). */
export function areaBbox(a: Area): Bbox {
  if (a.kind === 'place') return a.bbox
  if (a.kind === 'polygon') return geometryBbox(a.geometry)
  const dLat = a.radiusKm / KM_PER_DEGREE
  const dLng = a.radiusKm / (KM_PER_DEGREE * Math.max(0.01, Math.cos((a.center.lat * Math.PI) / 180)))
  return [
    Math.max(-180, a.center.lng - dLng),
    Math.max(-90, a.center.lat - dLat),
    Math.min(180, a.center.lng + dLng),
    Math.min(90, a.center.lat + dLat),
  ]
}

export const areaPointCount = (a: Area): number => (a.kind === 'circle' ? 0 : geometryPointCount(a.geometry))

/**
 * Rounds coordinates to 5 decimals and drops rings / polygons that collapsed below 4 positions or to a zero area
 * (simplification can flatten a thin ring onto a line; `areaGeometryIssues` rejects such rings).
 */
export function roundGeometry(g: AreaGeometry): AreaGeometry | null {
  const clean = (poly: Position[][]): Position[][] | null => {
    const rings = poly
      .map((ring) => {
        const out: Position[] = []
        for (const [lng, lat] of ring) {
          const p: Position = [round(lng), round(lat)]
          const prev = out[out.length - 1]
          if (!prev || prev[0] !== p[0] || prev[1] !== p[1]) out.push(p)
        }
        if (out.length && (out[0]![0] !== out[out.length - 1]![0] || out[0]![1] !== out[out.length - 1]![1]))
          out.push([...out[0]!] as Position)
        return out
      })
      .map((ring) => (ring.length >= 4 && !isZeroAreaRing(ring) ? ring : []))
    // Outer ring collapsed ⇒ drop the whole polygon; collapsed holes are dropped.
    if (!rings[0] || rings[0].length < 4) return null
    return rings.filter((r) => r.length >= 4)
  }
  if (g.type === 'Polygon') {
    const p = clean(g.coordinates as Position[][])
    return p ? { type: 'Polygon', coordinates: p } : null
  }
  const polys = (g.coordinates as Position[][][]).map(clean).filter((p): p is Position[][] => p !== null)
  if (!polys.length) return null
  return polys.length === 1 ? { type: 'Polygon', coordinates: polys[0]! } : { type: 'MultiPolygon', coordinates: polys }
}

export interface SimplifiedGeometry {
  geometry: AreaGeometry
  /** Tolerance used (degrees), 0 when only rounded. */
  toleranceDeg: number
  points: number
  bytes: number
}

/**
 * Round to 5 decimals, then Douglas-Peucker (@turf/simplify) with a doubling tolerance until the geometry fits the
 * caps (default: the stored-area caps, 5 000 points / 100 KB). Throws when it cannot (degenerate input).
 */
export function simplifyGeometryToCaps(
  input: AreaGeometry,
  opts: { maxPoints?: number; maxBytes?: number; startToleranceDeg?: number } = {}
): SimplifiedGeometry {
  const maxPoints = opts.maxPoints ?? MAX_AREA_POINTS
  const maxBytes = opts.maxBytes ?? MAX_AREA_BYTES
  let tolerance = 0
  let next = opts.startToleranceDeg && opts.startToleranceDeg > 0 ? opts.startToleranceDeg : 0.0001
  for (let i = 0; i < 24; i++) {
    const simplified =
      tolerance > 0 ? (simplify(input as never, { tolerance, highQuality: false }) as unknown as AreaGeometry) : input
    const g = roundGeometry(simplified)
    if (g) {
      const points = geometryPointCount(g)
      const bytes = geometryBytes(g)
      if (points <= maxPoints && bytes <= maxBytes) return { geometry: g, toleranceDeg: tolerance, points, bytes }
    }
    tolerance = tolerance === 0 ? next : tolerance * 2
    next = tolerance
  }
  throw new Error('Geometry cannot be simplified to the size limits')
}

/* ------------------------------------------------------------------ public projection */

/** Public-safe area (landing map): label + simplified geometry; never the OSM metadata. */
export const PublicAreaSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('place'),
    label: z.string(),
    countryIso3: z.string().nullable(),
    geometry: z.discriminatedUnion('type', [PolygonGeometrySchema, MultiPolygonGeometrySchema]),
    bbox: BboxSchema,
  }),
  z.object({
    kind: z.literal('polygon'),
    label: z.string(),
    geometry: z.discriminatedUnion('type', [PolygonGeometrySchema, MultiPolygonGeometrySchema]),
    bbox: BboxSchema,
  }),
  z.object({
    kind: z.literal('circle'),
    label: z.string(),
    center: LatLngSchema,
    radiusKm: z.number(),
    bbox: BboxSchema,
  }),
])
export type PublicArea = z.infer<typeof PublicAreaSchema>

/** Area for public pages / member map overlays: geometry simplified to ≤1 000 points / 30 KB. */
export function publicArea(a: Area): PublicArea {
  if (a.kind === 'circle')
    return { kind: 'circle', label: a.label, center: a.center, radiusKm: a.radiusKm, bbox: areaBbox(a) }
  let geometry: AreaGeometry = a.geometry
  try {
    geometry = simplifyGeometryToCaps(a.geometry, {
      maxPoints: MAX_PUBLIC_AREA_POINTS,
      maxBytes: MAX_PUBLIC_AREA_BYTES,
    }).geometry
  } catch {
    // keep the stored geometry (already within the stored caps)
  }
  return a.kind === 'place'
    ? { kind: 'place', label: a.label, countryIso3: a.countryIso3, geometry, bbox: a.bbox }
    : { kind: 'polygon', label: a.label, geometry, bbox: geometryBbox(a.geometry) }
}

/** Approximate size of an area in km (bbox diagonal / circle diameter): used by the R15 warnings. */
export function areaExtentKm(a: Area): number {
  if (a.kind === 'circle') return a.radiusKm * 2
  const [w, s, e, n] = areaBbox(a)
  const midLat = ((s + n) / 2) * (Math.PI / 180)
  const dx = (e - w) * KM_PER_DEGREE * Math.cos(midLat)
  const dy = (n - s) * KM_PER_DEGREE
  return Math.sqrt(dx * dx + dy * dy)
}

/** Longitude wrapped into [-180, 180) (world-wrapped maps report longitudes beyond ±180). */
export const normalizeLng = (lng: number): number => {
  const v = (((lng + 540) % 360) + 360) % 360
  return v - 180
}

/* ------------------------------------------------------------------ validity (request bodies) */

/*
 * Geometric validity of the areas an organizer SUBMITS (PLAN v4 P3): every ring must enclose a surface (≥3 distinct
 * positions not on one line); a DRAWN polygon must also be simple (its outline never crosses or touches itself) and its
 * holes must lie inside the outline without touching it or each other. Place areas (simplified OSM boundaries, where
 * Douglas-Peucker can create tiny self-intersections) only get the zero-area check.
 *
 * Request bodies only (`RequirementsSchema`, `requirementsIssues`): stored requirements are never re-checked on read,
 * so older rows keep parsing. Computed on coordinates rounded to 5 decimals (integer units of 1e-5°, exact
 * arithmetic), the precision areas are stored with.
 */

type Pt = readonly [number, number]
const UNIT = 10 ** COORDINATE_DECIMALS

/** Ring in integer units (1e-5°), consecutive duplicates and the closing position removed. */
function unitRing(ring: readonly (readonly number[])[]): Pt[] {
  const out: Pt[] = []
  for (const p of ring) {
    const q: Pt = [Math.round(p[0]! * UNIT), Math.round(p[1]! * UNIT)]
    const prev = out[out.length - 1]
    if (!prev || prev[0] !== q[0] || prev[1] !== q[1]) out.push(q)
  }
  while (out.length > 1 && out[0]![0] === out[out.length - 1]![0] && out[0]![1] === out[out.length - 1]![1]) out.pop()
  return out
}

const orient = (a: Pt, b: Pt, c: Pt): number => {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
  return v > 0 ? 1 : v < 0 ? -1 : 0
}

/**
 * No surface: fewer than 3 distinct positions, or all of them on one line. (Not the signed area: the two lobes of a
 * self-crossing "bow tie" cancel out, yet it encloses a surface.)
 */
function flat(r: Pt[]): boolean {
  if (r.length < 3) return true
  const [a, b] = r as [Pt, Pt]
  for (let i = 2; i < r.length; i++) if (orient(a, b, r[i]!) !== 0) return false
  return true
}

/** A ring without surface: fewer than 3 distinct positions, or every position on one line (5-decimal precision). */
export const isZeroAreaRing = (ring: readonly (readonly number[])[]): boolean => flat(unitRing(ring))

/** c lies within the bounding box of ab (c collinear with ab ⇒ on the segment). */
const within = (a: Pt, b: Pt, c: Pt): boolean =>
  Math.min(a[0], b[0]) <= c[0] &&
  c[0] <= Math.max(a[0], b[0]) &&
  Math.min(a[1], b[1]) <= c[1] &&
  c[1] <= Math.max(a[1], b[1])

/** Closed segments ab and cd share at least one point (touching included). */
function segmentsTouch(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c)
  const o2 = orient(a, b, d)
  const o3 = orient(c, d, a)
  const o4 = orient(c, d, b)
  if (o1 !== o2 && o3 !== o4) return true
  return (
    (o1 === 0 && within(a, b, c)) ||
    (o2 === 0 && within(a, b, d)) ||
    (o3 === 0 && within(c, d, a)) ||
    (o4 === 0 && within(c, d, b))
  )
}

interface Seg {
  a: Pt
  b: Pt
  ring: number
  /** Index in its ring (segment i joins vertex i and i+1, the last one closes the ring). */
  i: number
  minX: number
  maxX: number
  minY: number
  maxY: number
}

/**
 * First pair of touching segments among a polygon's rings. Two consecutive segments of one ring share a vertex: that
 * only counts when they fold back onto each other (a spike). Sweep on x: O(n log n) for usual shapes, O(n²) at worst
 * (n ≤ MAX_AREA_POINTS).
 */
function firstTouch(rings: Pt[][]): { ring: number; other: number } | null {
  const segs: Seg[] = []
  rings.forEach((r, ring) => {
    for (let i = 0; i < r.length; i++) {
      const a = r[i]!
      const b = r[(i + 1) % r.length]!
      segs.push({
        a,
        b,
        ring,
        i,
        minX: Math.min(a[0], b[0]),
        maxX: Math.max(a[0], b[0]),
        minY: Math.min(a[1], b[1]),
        maxY: Math.max(a[1], b[1]),
      })
    }
  })
  segs.sort((s, t) => s.minX - t.minX)
  for (let x = 0; x < segs.length; x++) {
    const s = segs[x]!
    for (let y = x + 1; y < segs.length; y++) {
      const t = segs[y]!
      if (t.minX > s.maxX) break
      if (t.minY > s.maxY || t.maxY < s.minY) continue
      if (s.ring === t.ring) {
        const n = rings[s.ring]!.length
        const [lo, hi] = s.i < t.i ? [s, t] : [t, s]
        // lo → hi consecutive (shared vertex lo.b = hi.a), or the closing segment hi → lo (shared hi.b = lo.a).
        const pair =
          hi.i === lo.i + 1
            ? ([lo.a, lo.b, hi.b] as const)
            : lo.i === 0 && hi.i === n - 1
              ? ([hi.a, lo.a, lo.b] as const)
              : null
        if (pair) {
          const [p, v, q] = pair
          const foldsBack = orient(p, v, q) === 0 && (p[0] - v[0]) * (q[0] - v[0]) + (p[1] - v[1]) * (q[1] - v[1]) > 0
          if (foldsBack) return { ring: s.ring, other: t.ring }
          continue
        }
      }
      if (segmentsTouch(s.a, s.b, t.a, t.b)) return { ring: s.ring, other: t.ring }
    }
  }
  return null
}

/** Point strictly inside a ring (ray casting; the point is known not to lie on the ring). */
function insideRing(p: Pt, r: Pt[]): boolean {
  let inside = false
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i]!
    const [xj, yj] = r[j]!
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

export interface AreaGeometryIssue {
  /** Relative to the area (`['geometry', 'coordinates', …ring]`). */
  path: (string | number)[]
  message: string
}

export const ZERO_AREA_MESSAGE = 'The shape has no surface: it needs at least 3 points that are not on one line'
export const SELF_INTERSECTION_MESSAGE =
  'The outline crosses or touches itself: redraw the shape without crossing lines'
export const INVALID_HOLE_MESSAGE = 'A hole must lie inside the outline without touching it or other holes'

/** One polygon's rings (`coordinates` of a Polygon): strict (drawn) or zero-area only (place). */
function polygonIssues(poly: Position[][], strict: boolean, path: (string | number)[]): AreaGeometryIssue[] {
  const rings = poly.map(unitRing)
  for (let k = 0; k < rings.length; k++) {
    const r = rings[k]!
    if (flat(r))
      return [{ path: [...path, k], message: k === 0 ? ZERO_AREA_MESSAGE : 'A hole of the shape has no surface' }]
  }
  if (!strict) return []
  const touch = firstTouch(rings)
  if (touch)
    return [
      {
        path: [...path, touch.ring === 0 ? touch.other : touch.ring],
        message: touch.ring === 0 && touch.other === 0 ? SELF_INTERSECTION_MESSAGE : INVALID_HOLE_MESSAGE,
      },
    ]
  // No crossing: one vertex tells whether a hole is inside the outline / inside another hole.
  for (let k = 1; k < rings.length; k++) {
    const v = rings[k]![0]!
    const nested = rings.some((other, m) => m > 0 && m !== k && insideRing(v, other))
    if (nested || !insideRing(v, rings[0]!)) return [{ path: [...path, k], message: INVALID_HOLE_MESSAGE }]
  }
  return []
}

/**
 * Validity issues of one area (empty ⇒ valid): drawn polygons strictly (simple outline, holes inside), place areas only
 * zero-area rings, circles none. Geometries over MAX_AREA_POINTS are skipped (the caps already reject them).
 */
export function areaGeometryIssues(a: Area): AreaGeometryIssue[] {
  if (a.kind === 'circle') return []
  const g = a.geometry
  if (geometryPointCount(g) > MAX_AREA_POINTS) return []
  const strict = a.kind === 'polygon'
  if (g.type === 'Polygon') return polygonIssues(g.coordinates as Position[][], strict, ['geometry', 'coordinates'])
  const polys = g.coordinates as Position[][][]
  for (let i = 0; i < polys.length; i++) {
    const issues = polygonIssues(polys[i]!, strict, ['geometry', 'coordinates', i])
    if (issues.length) return issues
  }
  return []
}
